import { useState, useEffect } from "react";
import { Menu, X, Shield } from "lucide-react";
import { Button } from "./ui/button";

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? "bg-white/95 backdrop-blur-md border-b border-slate-200 py-3 shadow-sm" 
          : "bg-transparent py-4"
      }`}
      data-testid="header"
    >
      <div className="max-w-[1400px] mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => scrollToSection("hero")}
          data-testid="logo"
        >
          <div className={`w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg ${isScrolled ? 'shadow-emerald-200' : 'shadow-emerald-500/30'}`}>
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <span className={`font-heading text-xl tracking-tight ${isScrolled ? 'text-slate-900' : 'text-white'}`}>SHINGLE</span>
            <span className={`font-heading text-xl tracking-tight ${isScrolled ? 'text-emerald-600' : 'text-emerald-400'}`}>CHECK</span>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
          <button
            onClick={() => scrollToSection("view-roof")}
            className={`transition-colors text-sm font-medium ${isScrolled ? 'text-slate-600 hover:text-emerald-600' : 'text-white/80 hover:text-white'}`}
          >
            View My Roof
          </button>
          <button
            onClick={() => scrollToSection("upload-estimate")}
            className={`transition-colors text-sm font-medium ${isScrolled ? 'text-slate-600 hover:text-emerald-600' : 'text-white/80 hover:text-white'}`}
          >
            Estimate Review
          </button>
          <button
            onClick={() => scrollToSection("services")}
            className={`transition-colors text-sm font-medium ${isScrolled ? 'text-slate-600 hover:text-emerald-600' : 'text-white/80 hover:text-white'}`}
          >
            Services
          </button>
          <button
            onClick={() => scrollToSection("storm-center")}
            className={`transition-colors text-sm font-medium ${isScrolled ? 'text-slate-600 hover:text-emerald-600' : 'text-white/80 hover:text-white'}`}
          >
            Storm Center
          </button>
          <button
            onClick={() => scrollToSection("roof-form")}
            className={`transition-colors text-sm font-medium ${isScrolled ? 'text-slate-600 hover:text-emerald-600' : 'text-white/80 hover:text-white'}`}
          >
            Get Quote
          </button>
          <Button
            onClick={() => scrollToSection("booking")}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full font-semibold px-6 py-2 transition-all duration-300 shadow-lg shadow-emerald-200"
            data-testid="header-cta-btn"
          >
            Free Inspection
          </Button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden text-slate-900"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden absolute top-full left-0 right-0 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-lg"
          data-testid="mobile-menu"
        >
          <nav className="flex flex-col p-6 gap-4">
            <button
              onClick={() => scrollToSection("view-roof")}
              className="text-slate-700 hover:text-emerald-600 transition-colors text-sm font-medium text-left py-2"
            >
              View My Roof
            </button>
            <button
              onClick={() => scrollToSection("upload-estimate")}
              className="text-slate-700 hover:text-emerald-600 transition-colors text-sm font-medium text-left py-2"
            >
              Estimate Review
            </button>
            <button
              onClick={() => scrollToSection("services")}
              className="text-slate-700 hover:text-emerald-600 transition-colors text-sm font-medium text-left py-2"
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection("storm-center")}
              className="text-slate-700 hover:text-emerald-600 transition-colors text-sm font-medium text-left py-2"
            >
              Storm Center
            </button>
            <button
              onClick={() => scrollToSection("booking")}
              className="text-slate-700 hover:text-emerald-600 transition-colors text-sm font-medium text-left py-2"
            >
              Schedule
            </button>
            <Button
              onClick={() => scrollToSection("booking")}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full font-semibold w-full mt-2"
              data-testid="mobile-cta-btn"
            >
              Free Inspection
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}
